import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Container
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { verifyEmail } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuthData } = useAuth();
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [message, setMessage] = useState('');
  const [verificationAttempted, setVerificationAttempted] = useState(false);

  const token = searchParams.get('token');

  useEffect(() => {
    const handleVerification = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link. No token provided.');
        return;
      }

      // Prevent duplicate verification attempts
      if (verificationAttempted) {
        return;
      }
      setVerificationAttempted(true);

      try {
        const response = await verifyEmail(token);
        
        // Set auth data from response (auto-login after verification)
        setAuthData(response.data.access_token, response.data.user);
        
        setStatus('success');
        setMessage('Email verified successfully! You are now logged in.');
        
        // Redirect to dashboard after 3 seconds
        setTimeout(() => {
          navigate('/dashboard', { 
            state: { message: 'Email verified successfully! Welcome to examr.' }
          });
        }, 3000);
        
      } catch (err) {
        setStatus('error');
        if (err.response?.status === 400) {
          setMessage('Invalid or expired verification token. Please request a new verification email.');
        } else {
          setMessage('Email verification failed. Please try again or contact support.');
        }
      }
    };

    handleVerification();
  }, [token, navigate, setAuthData, verificationAttempted]);

  const handleResendVerification = () => {
    navigate('/resend-verification');
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <Container maxWidth="sm">
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        py={4}
      >
        <Paper elevation={3} sx={{ width: '100%', p: 4 }}>
          <Box textAlign="center" mb={4}>
            <Typography variant="h4" component="h1" gutterBottom>
              Email Verification
            </Typography>
          </Box>

          <Card>
            <CardContent>
              <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
                {status === 'verifying' && (
                  <>
                    <CircularProgress size={60} sx={{ mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      Verifying your email...
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Please wait while we confirm your email address.
                    </Typography>
                  </>
                )}

                {status === 'success' && (
                  <>
                    <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
                    <Typography variant="h6" gutterBottom color="success.main">
                      Email Verified Successfully!
                    </Typography>
                    <Alert severity="success" sx={{ mt: 2, mb: 3 }}>
                      {message}
                    </Alert>
                    <Typography variant="body2" color="text.secondary">
                      Redirecting you to the dashboard...
                    </Typography>
                  </>
                )}

                {status === 'error' && (
                  <>
                    <ErrorIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
                    <Typography variant="h6" gutterBottom color="error.main">
                      Verification Failed
                    </Typography>
                    <Alert severity="error" sx={{ mt: 2, mb: 3 }}>
                      {message}
                    </Alert>
                    <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
                      <Button
                        variant="outlined"
                        onClick={handleResendVerification}
                        size="large"
                      >
                        Resend Verification Email
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleBackToLogin}
                        size="large"
                      >
                        Back to Login
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
            </CardContent>
          </Card>

          {status === 'success' && (
            <Box mt={3} textAlign="center">
              <Typography variant="body2" color="text.secondary">
                🎉 Welcome to examr! Your account is now fully activated and you can start generating exams.
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default VerifyEmail;